import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Edit3, 
  History, 
  Filter, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Users,
  Package,
  Calendar,
  Percent,
  Minus,
  Plus,
  Save,
  X,
  Eye,
  Mail,
  RefreshCw
} from 'lucide-react';
import './PriceControlModule.css';

const PriceControlModule = ({ refreshTick = 0 }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [toast, setToast] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    vendor_id: '',
    category: '',
    status: 'active'
  });
  
  // Bulk update form
  const [bulkForm, setBulkForm] = useState({
    update_type: 'percentage_increase',
    value: '',
    reason: '',
    min_price: '',
    max_price: ''
  });
  
  // Individual update form
  const [individualForm, setIndividualForm] = useState({
    new_price: '',
    reason: '',
    min_price: '',
    max_price: ''
  });

  const adminKey = localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey') || localStorage.getItem('token');

  // Fetch products for price control
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        ...(filters.search && { search: filters.search }),
        ...(filters.vendor_id && { vendor_id: filters.vendor_id }),
        ...(filters.category && { category: filters.category }),
        ...(filters.status && { status: filters.status })
      });

      const response = await fetch(`/api/admin/products?${params}`, {
        headers: { 'x-admin-key': adminKey }
      });

      if (response.ok) {
        const data = await response.json();
        const productList = Array.isArray(data) ? data : 
                          Array.isArray(data?.items) ? data.items :
                          Array.isArray(data?.products) ? data.products : [];
        setProducts(productList);
      } else {
        console.error('Failed to fetch products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Update individual product price
  const updateIndividualPrice = async (productId, formData) => {
    try {
      const response = await fetch(`/api/admin/products/${productId}/price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage({
          type: 'success',
          title: 'Price Updated Successfully!',
          message: `"${selectedProduct?.name}" price has been updated. Vendor ${result.vendor_notified ? 'has been notified' : 'notification failed'}.`,
          details: `Old Price: ₹${result.old_price} → New Price: ₹${result.new_price}`
        });
        // Auto-dismiss after 8 seconds
        setTimeout(() => setSuccessMessage(null), 8000);
        setToast({
          type: 'success',
          message: `Price updated successfully. Vendor ${result.vendor_notified ? 'notified' : 'not notified'}.`
        });
        fetchProducts();
        setShowIndividualModal(false);
        setSelectedProduct(null);
      } else {
        const error = await response.json();
        setToast({
          type: 'error',
          message: error.error || 'Failed to update price'
        });
      }
    } catch (error) {
      console.error('Error updating price:', error);
      setToast({
        type: 'error',
        message: 'Failed to update price'
      });
    }
  };

  // Bulk price update
  const performBulkUpdate = async () => {
    try {
      const productIds = selectedProducts.length > 0 ? selectedProducts : products.map(p => p.id);
      
      const response = await fetch('/api/admin/products/bulk-price-update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({
          product_ids: productIds,
          update_type: bulkForm.update_type,
          value: parseFloat(bulkForm.value),
          reason: bulkForm.reason,
          filters: filters,
          min_price: bulkForm.min_price ? parseFloat(bulkForm.min_price) : null,
          max_price: bulkForm.max_price ? parseFloat(bulkForm.max_price) : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage({
          type: 'success',
          title: 'Bulk Price Update Completed!',
          message: `Successfully updated ${result.successful_updates} products. ${result.failed_updates} updates failed.`,
          details: `${result.vendors_notified} vendors have been notified of the changes.`
        });
        // Auto-dismiss after 8 seconds
        setTimeout(() => setSuccessMessage(null), 8000);
        setToast({
          type: 'success',
          message: `Bulk update completed: ${result.successful_updates} successful, ${result.failed_updates} failed. ${result.vendors_notified} vendors notified.`
        });
        fetchProducts();
        setShowBulkModal(false);
        setSelectedProducts([]);
        setBulkForm({
          update_type: 'percentage_increase',
          value: '',
          reason: '',
          min_price: '',
          max_price: ''
        });
      } else {
        const error = await response.json();
        setToast({
          type: 'error',
          message: error.error || 'Failed to perform bulk update'
        });
      }
    } catch (error) {
      console.error('Error performing bulk update:', error);
      setToast({
        type: 'error',
        message: 'Failed to perform bulk update'
      });
    }
  };

  // Fetch price history
  const fetchPriceHistory = async (productId) => {
    try {
      const response = await fetch(`/api/admin/products/${productId}/price-history?limit=50`, {
        headers: { 'x-admin-key': adminKey }
      });

      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.history || []);
      } else {
        setPriceHistory([]);
      }
    } catch (error) {
      console.error('Error fetching price history:', error);
      setPriceHistory([]);
    }
  };

  // Handle product selection for bulk operations
  const handleProductSelect = (productId, checked) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Calculate new price preview for bulk update
  const calculateNewPrice = (currentPrice, updateType, value) => {
    if (!currentPrice || !value) return currentPrice;
    
    const currentPriceNum = parseFloat(currentPrice);
    const valueNum = parseFloat(value);
    
    if (isNaN(currentPriceNum) || isNaN(valueNum)) return currentPrice;
    
    switch (updateType) {
      case 'percentage_increase':
        return currentPriceNum * (1 + valueNum / 100);
      case 'percentage_decrease':
        return currentPriceNum * (1 - valueNum / 100);
      case 'fixed_increase':
        return currentPriceNum + valueNum;
      case 'fixed_decrease':
        return currentPriceNum - valueNum;
      case 'set_price':
        return valueNum;
      default:
        return currentPriceNum;
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filters, refreshTick]);

  const updateTypes = [
    { value: 'percentage_increase', label: 'Increase by %', icon: TrendingUp },
    { value: 'percentage_decrease', label: 'Decrease by %', icon: TrendingDown },
    { value: 'fixed_increase', label: 'Increase by ₹', icon: Plus },
    { value: 'fixed_decrease', label: 'Decrease by ₹', icon: Minus },
    { value: 'set_price', label: 'Set to ₹', icon: DollarSign }
  ];

  return (
    <div className="price-control-module">
      <div className="module-header">
        <h2>Price Control Module</h2>
        <p>Manage product pricing with individual updates, bulk operations, and comprehensive audit trails.</p>
        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={fetchProducts}
            disabled={loading}
          >
            <RefreshCw size={16} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowBulkModal(true)}
            disabled={products.length === 0}
          >
            <TrendingUp size={16} />
            Bulk Update
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search products..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            className="form-input"
          />
        </div>
        <div className="filter-group">
          <input
            type="text"
            placeholder="Vendor ID"
            value={filters.vendor_id}
            onChange={(e) => setFilters({...filters, vendor_id: e.target.value})}
            className="form-input"
          />
        </div>
        <div className="filter-group">
          <input
            type="text"
            placeholder="Category"
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
            className="form-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="form-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
      </div>

      {/* Success Message Banner */}
      {successMessage && (
        <div className="success-banner">
          <div className="success-banner-content">
            <div className="success-icon">
              <CheckCircle size={24} />
            </div>
            <div className="success-text">
              <h4>{successMessage.title}</h4>
              <p>{successMessage.message}</p>
              {successMessage.details && (
                <p className="success-details">{successMessage.details}</p>
              )}
            </div>
            <button 
              className="success-close-btn"
              onClick={() => setSuccessMessage(null)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="table-container">
        <table className="price-control-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedProducts.length === products.length && products.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>Product</th>
              <th>Vendor</th>
              <th>Current Price</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="loading-cell">Loading products...</td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-cell">No products found</td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => handleProductSelect(product.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <div className="product-info">
                      <div className="product-name">{product.name}</div>
                      <div className="product-sku">SKU: {product.sku}</div>
                    </div>
                  </td>
                  <td>
                    <div className="vendor-info">
                      <div className="vendor-name">{product.vendor_name || 'Unknown'}</div>
                      <div className="vendor-id">ID: {product.vendor_id}</div>
                    </div>
                  </td>
                  <td>
                    <div className="price-display">
                      <span className="current-price">₹{parseFloat(product.price || 0).toFixed(2)}</span>
                      {product.mrp && (
                        <span className="mrp-price">MRP: ₹{parseFloat(product.mrp).toFixed(2)}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{product.category}</span>
                  </td>
                  <td>
                    <span className={`status-badge status-${product.status}`}>
                      {product.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setSelectedProduct(product);
                          setIndividualForm({
                            new_price: product.price,
                            reason: '',
                            min_price: '',
                            max_price: ''
                          });
                          setShowIndividualModal(true);
                        }}
                        title="Edit Price"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setSelectedProduct(product);
                          fetchPriceHistory(product.id);
                          setShowHistoryModal(true);
                        }}
                        title="View History"
                      >
                        <History size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Individual Price Update Modal */}
      {showIndividualModal && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Update Price - {selectedProduct.name}</h3>
              <button className="close-btn" onClick={() => setShowIndividualModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Current Price</label>
                <div className="current-price-display">₹{parseFloat(selectedProduct.price || 0).toFixed(2)}</div>
              </div>
              <div className="form-group">
                <label>New Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={individualForm.new_price}
                  onChange={(e) => setIndividualForm({...individualForm, new_price: e.target.value})}
                  className="form-input"
                  placeholder="Enter new price"
                />
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea
                  value={individualForm.reason}
                  onChange={(e) => setIndividualForm({...individualForm, reason: e.target.value})}
                  className="form-textarea"
                  placeholder="Reason for price change (optional)"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Min Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={individualForm.min_price}
                    onChange={(e) => setIndividualForm({...individualForm, min_price: e.target.value})}
                    className="form-input"
                    placeholder="Minimum price"
                  />
                </div>
                <div className="form-group">
                  <label>Max Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={individualForm.max_price}
                    onChange={(e) => setIndividualForm({...individualForm, max_price: e.target.value})}
                    className="form-input"
                    placeholder="Maximum price"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowIndividualModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => updateIndividualPrice(selectedProduct.id, individualForm)}
                disabled={!individualForm.new_price || isNaN(individualForm.new_price)}
              >
                <Save size={16} />
                Update Price
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Price Update Modal */}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h3>Bulk Price Update</h3>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="bulk-update-info">
                <div className="info-card">
                  <Package size={20} />
                  <span>{selectedProducts.length > 0 ? selectedProducts.length : products.length} products selected</span>
                </div>
                <div className="info-card">
                  <Users size={20} />
                  <span>{new Set(products.map(p => p.vendor_id)).size} vendors affected</span>
                </div>
              </div>

              <div className="form-group">
                <label>Update Type *</label>
                <div className="update-type-grid">
                  {updateTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        className={`update-type-btn ${bulkForm.update_type === type.value ? 'active' : ''}`}
                        onClick={() => setBulkForm({...bulkForm, update_type: type.value})}
                      >
                        <Icon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>
                  {bulkForm.update_type.includes('percentage') ? 'Percentage' : 
                   bulkForm.update_type.includes('set') ? 'New Price' : 'Amount'} *
                </label>
                <input
                  type="number"
                  step={bulkForm.update_type.includes('percentage') ? '0.01' : '0.01'}
                  min="0"
                  value={bulkForm.value}
                  onChange={(e) => setBulkForm({...bulkForm, value: e.target.value})}
                  className="form-input"
                  placeholder={
                    bulkForm.update_type.includes('percentage') ? 'Enter percentage' :
                    bulkForm.update_type.includes('set') ? 'Enter new price' : 'Enter amount'
                  }
                />
              </div>

              <div className="form-group">
                <label>Reason</label>
                <textarea
                  value={bulkForm.reason}
                  onChange={(e) => setBulkForm({...bulkForm, reason: e.target.value})}
                  className="form-textarea"
                  placeholder="Reason for bulk price update (optional)"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Min Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkForm.min_price}
                    onChange={(e) => setBulkForm({...bulkForm, min_price: e.target.value})}
                    className="form-input"
                    placeholder="Minimum price"
                  />
                </div>
                <div className="form-group">
                  <label>Max Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkForm.max_price}
                    onChange={(e) => setBulkForm({...bulkForm, max_price: e.target.value})}
                    className="form-input"
                    placeholder="Maximum price"
                  />
                </div>
              </div>

              {/* Price Preview */}
              {bulkForm.value && !isNaN(parseFloat(bulkForm.value)) && (
                <div className="price-preview">
                  <h4>Price Preview (First 5 Products)</h4>
                  <div className="preview-table">
                    {products.slice(0, 5).map((product) => {
                      const currentPrice = parseFloat(product.price) || 0;
                      const newPrice = calculateNewPrice(currentPrice, bulkForm.update_type, parseFloat(bulkForm.value));
                      const change = newPrice - currentPrice;
                      const changePercent = currentPrice > 0 ? ((change / currentPrice) * 100).toFixed(1) : '0.0';
                      
                      return (
                        <div key={product.id} className="preview-row">
                          <div className="product-name">{product.name}</div>
                          <div className="price-change">
                            <span className="old-price">₹{currentPrice.toFixed(2)}</span>
                            <span className="arrow">→</span>
                            <span className="new-price">₹{newPrice.toFixed(2)}</span>
                            <span className={`change ${change >= 0 ? 'positive' : 'negative'}`}>
                              {change >= 0 ? '+' : ''}{changePercent}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={performBulkUpdate}
                disabled={!bulkForm.value || isNaN(bulkForm.value)}
              >
                <TrendingUp size={16} />
                Apply Bulk Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price History Modal */}
      {showHistoryModal && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h3>Price History - {selectedProduct.name}</h3>
              <button className="close-btn" onClick={() => setShowHistoryModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="history-table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Old Price</th>
                      <th>New Price</th>
                      <th>Change</th>
                      <th>Reason</th>
                      <th>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceHistory.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="empty-cell">No price history available</td>
                      </tr>
                    ) : (
                      priceHistory.map((entry) => {
                        const oldPrice = parseFloat(entry.old_price || 0);
                        const newPrice = parseFloat(entry.new_price || 0);
                        const change = newPrice - oldPrice;
                        const changePercent = oldPrice > 0 ? ((change / oldPrice) * 100).toFixed(1) : '0.0';
                        
                        return (
                          <tr key={entry.id}>
                            <td>{new Date(entry.created_at).toLocaleString()}</td>
                            <td>₹{parseFloat(entry.old_price || 0).toFixed(2)}</td>
                            <td>₹{parseFloat(entry.new_price || 0).toFixed(2)}</td>
                            <td>
                              <span className={`change ${change >= 0 ? 'positive' : 'negative'}`}>
                                {change >= 0 ? '+' : ''}₹{change.toFixed(2)} ({changePercent}%)
                              </span>
                            </td>
                            <td>{entry.reason || 'No reason provided'}</td>
                            <td>
                              <span className="changed-by">{entry.changed_by}</span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowHistoryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className={`toast ${toast.type}`} onAnimationEnd={() => setToast(null)}>
          <div className="toast-content">
            {toast.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceControlModule;
